import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { upsertContactUser } from '../../common/fetch.js';
import { accessRightsMsg, addUserMsg, selectCompanyHeaderMsg, t } from '../../common/i18n/index.js';
import { ContactFieldset } from '../../common/react-hook-form/fieldsets/ContactFieldset';
import { getPrimaryMdmId, isMultiBiz } from '../../common/utils/accountUtils.js';
import { isDuplicateContactError } from '../../common/utils/contactUtils';
import { isFeatureEnabled } from '../../common/utils/featureFlagUtils.js';
import { ContactRole, ContactType, UserProfile } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useFeatureFlags } from '../../public/site/ConfigProvider.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { ButtonGroupForSubmitAndBack } from '../ButtonGroupForSubmitAndBack/ButtonGroupForSubmitAndBack.js';
import { CompanySelector } from '../CompanySelector/CompanySelector.js';
import { DuplicateContactDialog } from '../Dialogs/DuplicateContactDialog';
import { getUserAccounts } from '../Header/dynamic/headerFunctions.js';
import { UserRolesAndAdminRights } from '../UserRolesAndAdminRights/UserRolesAndAdminRights.js';
import type { ContactFieldsetValues } from '../../common/react-hook-form/fieldsets/ContactFieldset';
import type { DialogParams } from '../../common/types/dialog.js';
import type { DuplicateContactResponse } from '../../common/utils/contactUtils.js';
import type { Contact, GenericErrorDuplicateContact, PutContactResponse } from '../../generated/api/models.js';
import type { ReactNode } from 'react';

import './CreateContactForm.scss';

interface ContactFormProps {
  children?: ReactNode;
  onSubmit: (values: ContactFieldsetValues) => void;
  readonly: boolean;
}

const ContactForm = ({ children, onSubmit, readonly }: ContactFormProps) => {
  const methods = useForm({
    mode: 'all',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      costCenter: '',
      employeeNumber: '',
    },
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        <h3>{t.AS6H('Personal information')}</h3>
        <ContactFieldset readonly={readonly} />
        {children}
      </form>
    </FormProvider>
  );
};

export const CreateContactForm = () => {
  const navigate = useNavigate();
  const featureFlags = useFeatureFlags();
  const [isSaving, setIsSaving] = useState(false);

  const [isNewContactApprover, setIsNewContactApprover] = useState(false);
  const [duplicateContactDialogVisible, setDuplicateContactDialogVisible] = useState(false);
  const [duplicateContact, setDuplicateContact] = useState<GenericErrorDuplicateContact | undefined>(undefined);

  const { authenticatedUser, elisaIdV3Enabled } = useAuth();

  const [mdmIdState, setMdmIdState] = useState<string | undefined>(undefined);

  const onShowDialog = (params: DialogParams) => openDialog(params);

  const onChangeApproverRole = (isApprover: boolean) => {
    setIsNewContactApprover(isApprover);
  };

  const generateNewContact = (values: ContactFieldsetValues, isApprover: boolean): Contact => {
    const roles = [ContactRole.ENDUSER_CONTACT, ...(isApprover ? [ContactRole.APPROVER] : [])];
    return {
      contactType: ContactType.PERSON,
      person: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        costCenter: values.costCenter,
        employeeNumber: values.employeeNumber,
        userProfile: UserProfile.EMPLOYEE,
        roles,
      },
    };
  };

  const handleCreateContactError = (responseData: PutContactResponse | DuplicateContactResponse) => {
    if (isDuplicateContactError(responseData)) {
      setDuplicateContact((responseData as DuplicateContactResponse).duplicateContact);
      setDuplicateContactDialogVisible(true);
    } else {
      addNotification(t.GXHR('Failed to create contact'), 'error');
    }
  };

  const onSubmit = async (values: ContactFieldsetValues) => {
    const updatedContactInformation = generateNewContact(values, isNewContactApprover);
    setIsSaving(true);

    const mdmId = (isMultiBiz(authenticatedUser) && (mdmIdState || getPrimaryMdmId(authenticatedUser))) || undefined;

    try {
      const response = await upsertContactUser(updatedContactInformation, false, false, true, mdmId);
      const responseData = await response.json();
      if (!response.ok) {
        handleCreateContactError(responseData);
        return;
      }
      addNotification(t.FFJ2('Changes were successfully saved'), 'success');

      const selectedMdmId = mdmIdState || getPrimaryMdmId(authenticatedUser);
      if (elisaIdV3Enabled && responseData.contactMasterId && selectedMdmId) {
        navigate(
          `${generatePath(paths.COMPANY_INFO_CONTACT, {
            contactMasterId: responseData.contactMasterId,
          })}?mdmId=${encodeURIComponent(selectedMdmId)}#access-rights-accordion`
        );
        return;
      }

      navigate(paths.COMPANY_INFO_CONTACTS);
    } catch (err) {
      addNotification(t.GXHR('Failed to create contact'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const onReset = () => {
    navigate(paths.COMPANY_INFO_CONTACTS);
  };

  return (
    <div className="of-create-new-contact__content">
      {duplicateContactDialogVisible && duplicateContact && (
        <DuplicateContactDialog
          duplicateContact={duplicateContact}
          hideUseContactDuplicateButton={true}
          onCloseDialog={() => setDuplicateContactDialogVisible(false)}
          onSelectDuplicateContact={() => {}}
        />
      )}
      {isMultiBiz(authenticatedUser) && (
        <>
          <h3 className={dsClass.MARGIN_TOP_7}>{t.BR9A(selectCompanyHeaderMsg)}</h3>
          <p>{t.A0OI('Select the company for which you want to create a user.')}</p>
          <CompanySelector
            userAccounts={getUserAccounts(authenticatedUser)}
            onInputChange={(_, option) => {
              setMdmIdState(option.accountMasterId!);
            }}
            initialMdmId={getPrimaryMdmId(authenticatedUser)}
          />
        </>
      )}
      <ContactForm readonly={false} onSubmit={onSubmit}>
        {elisaIdV3Enabled && (
          <CL.Disclaimer icon={<CL.Icon icon="information" />} className={dsClass.MARGIN_VERTICAL_6}>
            {t.XLP9('You can manage access rights after adding the user')}
          </CL.Disclaimer>
        )}
        {!elisaIdV3Enabled &&
          (isFeatureEnabled(featureFlags.showUserRightsAccordion) ||
            authenticatedUser?.enabledFeatureFlags?.includes('showUserRightsAccordion')) && (
            <>
              <h3 className="of-create-new-contact__h3-with-separator">{t.Y8LP(accessRightsMsg)}</h3>
              <UserRolesAndAdminRights
                isNewDisconnectedContact={true}
                ignoreAccordion={true}
                onShowDialog={onShowDialog}
                onChangeApproverRole={onChangeApproverRole}
                authenticatedUser={authenticatedUser!}
                mdmId={mdmIdState || getPrimaryMdmId(authenticatedUser)}
              />
            </>
          )}
        <div className="of-create-new-contact__options">
          <ButtonGroupForSubmitAndBack
            onCancel={onReset}
            onSubmit={() => {}}
            submitButtonText={t.V7KF(addUserMsg)}
            submitting={isSaving}
            submitOnLeft={true}
          />
        </div>
      </ContactForm>
    </div>
  );
};
